/**
 * Simplified memory cache handler
 */

import { EventEmitter } from '../base/event-emitter.js';
import { StorageRepositoryConfig } from '../config';
import { IAsyncHandler } from '../interfaces/handler.interface.js';
import {
  IAsyncRepository,
  IStatsProvider,
} from '../interfaces/repository.interface.js';
import type { TranslationData } from '../types/translation-data.types.js';
import type {
  TranslationContext,
  TranslationRequest,
  TranslationResult,
} from '../types/translation.types.js';
import { logger } from '../utils/index.js';
import {
  createNamespaceCacheKey,
  flattenTranslations,
  resolveNestedKey,
} from '../utils/translation-value-utils.js';

/**
 * Parsed cache entry containing both text and styles
 */
interface CachedContentUnit {
  text: string;
  styles: Record<string, string[]>;
}

/**
 * Async storage cache handler (IndexedDB, localStorage, etc.)
 */
export class AsyncHandler implements IAsyncHandler {
  readonly name: string;
  readonly version = '1.0.0';
  readonly priority: number;
  readonly type = 'storage' as const;
  readonly enabled: boolean;
  private ttlMap = new Map<string, number>();
  private ttl: number;
  private repository: IAsyncRepository & IStatsProvider;
  private bus: EventEmitter;

  constructor(
    eventEmitter: EventEmitter,
    repository: IAsyncRepository & IStatsProvider
  ) {
    this.bus = eventEmitter;
    this.repository = repository;
    this.name = repository.name;
    const config = repository.getConfig() as StorageRepositoryConfig;
    this.enabled = config.enabled ?? true;
    this.priority = config.priority ?? 0;
    this.ttl = config.options?.ttl ?? 30 * 60 * 1000; // Default 30 minutes
  }

  getSourceType(): 'memory' | 'storage' | 'network' | 'fallback' {
    return 'memory';
  }

  /**
   * Simple lookup implementation
   * Returns ContentUnit with both text and styles
   */
  async lookup(request: TranslationRequest): Promise<TranslationResult> {
    const startTime = Date.now();
    logger.debug(`Async lookup: ${request.cleanKey}`);
    const repoBus = this.bus.repo(request, this.name);

    const directResult = await this.tryDirectLookup(request);
    if (directResult !== null) {
      logger.debug(`Cache hit: ${request.cleanKey}`);
      repoBus.getSuccess(directResult.text);
      return this.createResult(directResult, 'storage', 200, startTime, true);
    }

    // Try namespace lookup for nested keys
    if (request.cleanKey.includes('.') && request.namespace) {
      const namespaceResult = await this.tryNamespaceLookup(request);
      if (namespaceResult !== null) {
        logger.debug(`Namespace hit: ${request.cleanKey}`);
        repoBus.getSuccess(namespaceResult.text);
        return this.createResult(
          namespaceResult,
          'storage',
          200,
          startTime,
          true
        );
      }
    }

    logger.debug(`Cache miss: ${request.cleanKey}`);
    repoBus.getMiss();

    // Return fallback result (no styles for fallback)
    return this.createResult(
      { text: request.parsedDefaultValue || request.cleanKey, styles: {} },
      'fallback',
      404,
      startTime
    );
  }

  /**
   * Simple save implementation
   * Stores ContentUnit (text + styles) as JSON
   */
  async save(
    request: TranslationRequest,
    result: TranslationResult
  ): Promise<void> {
    if (!result.value) return;
    const repoBus = this.bus.repo(request, this.name);

    // Store as ContentUnit JSON (text + styles together)
    const contentUnit: CachedContentUnit = {
      text: result.value,
      styles: result.styles || {},
    };
    await this.setWithTTL(request.individualCacheKey, JSON.stringify(contentUnit));

    logger.debug(`Saved to cache: ${request.cleanKey}`);
    repoBus.setSuccess(result.value);
  }

  /**
   * Namespace saving implementation
   */
  async saveNamespace(
    translations: TranslationData,
    context: TranslationContext
  ): Promise<void> {
    if (!context.namespace) return;
    const repoBus = this.bus.repo(
      context.locale + ':' + context.namespace,
      this.name
    );
    const keyCount = Object.keys(translations).length;
    logger.debug(
      `Saving namespace: ${context.locale}:${context.namespace} (${keyCount} keys)`
    );

    // Store full namespace: @locale:namespace
    const namespaceCacheKey = createNamespaceCacheKey(
      context.locale,
      context.namespace
    );
    await this.setWithTTL(namespaceCacheKey, JSON.stringify(translations));

    // Store flattened individual keys: locale:namespace:key
    const flattened = flattenTranslations(
      translations,
      '',
      context.locale,
      context.namespace!
    );

    let savedCount = 0;
    for (const [flatKey, flatValue] of Object.entries(flattened)) {
      await this.setWithTTL(flatKey, flatValue);
      savedCount++;
    }

    logger.debug(`Saved: ${savedCount} keys + namespace blob`);
    repoBus.setSuccess(JSON.stringify(translations));
  }

  /**
   * ✅ Strategy 1: Direct cache lookup using unified pattern
   * Returns parsed ContentUnit (text + styles) or null
   */
  private async tryDirectLookup(
    request: TranslationRequest
  ): Promise<CachedContentUnit | null> {
    // Check TTL and expire if needed
    if (await this.checkAndExpireTTL(request.individualCacheKey)) {
      return null;
    }

    const cachedValue = await this.repository.get(request.individualCacheKey);
    if (!cachedValue) return null;

    this.refreshTTL(request.individualCacheKey); // Refresh TTL on access

    // Parse cached ContentUnit JSON
    try {
      const parsed = JSON.parse(cachedValue);
      if (typeof parsed === 'object' && 'text' in parsed) {
        return {
          text: String(parsed.text),
          styles: parsed.styles || {},
        };
      }
      // Legacy format: plain string
      return { text: cachedValue, styles: {} };
    } catch {
      // If parse fails, treat as legacy plain string
      return { text: cachedValue, styles: {} };
    }
  }

  /**
   * ✅ Strategy 2: Namespace lookup using unified pattern
   * Returns parsed ContentUnit (text + styles) or null
   */
  private async tryNamespaceLookup(
    request: TranslationRequest
  ): Promise<CachedContentUnit | null> {
    if (!request.namespace) return null;

    const namespaceCacheKey = createNamespaceCacheKey(
      request.locale,
      request.namespace
    );

    // Check TTL for namespace
    if (await this.checkAndExpireTTL(namespaceCacheKey)) {
      return null;
    }

    const namespaceData = await this.repository.get(namespaceCacheKey);
    if (!namespaceData) return null;
    this.refreshTTL(namespaceCacheKey); // Refresh TTL on access

    try {
      const translations = JSON.parse(namespaceData);
      const value = resolveNestedKey(translations, request.cleanKey);

      if (value !== null && value !== undefined) {
        // Extract ContentUnit from namespace data
        let contentUnit: CachedContentUnit;

        if (typeof value === 'object' && value !== null && 'text' in value) {
          // Server ContentUnit format: { text, styles }
          contentUnit = {
            text: String((value as { text: unknown }).text),
            styles: (value as { styles?: Record<string, string[]> }).styles || {},
          };
        } else {
          // Plain string fallback
          contentUnit = { text: String(value), styles: {} };
        }

        // Cache the individual key for faster future access (as JSON)
        await this.setWithTTL(request.individualCacheKey, JSON.stringify(contentUnit));

        logger.debug(`Cached from namespace: ${request.cleanKey}`);
        return contentUnit;
      }
    } catch (error) {
      // Invalid JSON in namespace cache
      logger.debug(
        `Invalid JSON in namespace cache: ${namespaceCacheKey}`,
        error
      );
      await this.repository.remove(namespaceCacheKey);
      this.ttlMap.delete(namespaceCacheKey);
    }

    return null;
  }

  /**
   * Helper to create consistent TranslationResult objects
   * Now includes styles from ContentUnit
   */
  private createResult(
    contentUnit: CachedContentUnit,
    source: 'memory' | 'storage' | 'network' | 'fallback',
    status: number,
    startTime: number,
    cached?: boolean
  ): TranslationResult {
    return {
      value: contentUnit.text,
      styles: contentUnit.styles,
      source,
      status,
      duration: Date.now() - startTime,
      ...(cached !== undefined && { cached }),
    };
  }

  /**
   * Check TTL and expire if needed (async version)
   * Returns true if expired, false if still valid
   */
  private async checkAndExpireTTL(cacheKey: string): Promise<boolean> {
    const timestamp = this.ttlMap.get(cacheKey);
    if (timestamp && Date.now() - timestamp > this.ttl) {
      await this.repository.remove(cacheKey);
      this.ttlMap.delete(cacheKey);
      logger.debug(`TTL expired: ${cacheKey}`);
      return true;
    }
    return false;
  }

  /**
   * Set value in repository and update TTL (async version)
   */
  private async setWithTTL(cacheKey: string, value: string): Promise<void> {
    await this.repository.set(cacheKey, value);
    this.ttlMap.set(cacheKey, Date.now());
  }

  /**
   * Refresh TTL on access
   */
  private refreshTTL(cacheKey: string): void {
    this.ttlMap.set(cacheKey, Date.now());
  }

  /**
   * Get TTL information for debugging
   */
  getTTLInfo(): Record<
    string,
    { timestamp: number; age: number; remaining: number }
  > {
    const ttlInfo: Record<
      string,
      { timestamp: number; age: number; remaining: number }
    > = {};
    const now = Date.now();

    for (const [key, timestamp] of this.ttlMap.entries()) {
      const age = now - timestamp;
      const remaining = Math.max(0, this.ttl - age);
      ttlInfo[key] = { timestamp, age, remaining };
    }

    return ttlInfo;
  }

  async destroy(): Promise<void> {
    await this.repository.clear();
    this.ttlMap.clear();
  }

  getEntries(): Record<string, string> | undefined {
    if (this.repository.getEntries !== undefined) {
      return this.repository.getEntries();
    }
  }
}
